import { useCollections } from '../../features/catalog/queries.js';

/**
 * Turns each collection into the CSS tokens the shell already reads.
 *
 * The two original worlds had their accents written by hand in the token layer,
 * which is why adding a third was a code change. This emits the same
 * `[data-world="…"]` blocks from whatever the database holds, so a collection an
 * operator creates in the admin panel arrives fully dressed — accent, soft
 * accent, and its frame shape — with no deploy.
 *
 * The hand-written fragrance and sport blocks stay in `tailwind.css` as the
 * fallback that paints before this query resolves, so nothing flashes neutral on
 * first load. These rules come later in the cascade and win at equal specificity.
 *
 * Values are constrained at the API — `accent` and `accentSoft` must match a hex
 * pattern, `arch` and `cut` are bounded integers — so nothing arbitrary reaches
 * a stylesheet.
 */

const hex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const slugSafe = /^[a-z0-9-]+$/;

const rule = (collection) => {
  const { slug, accent, accentSoft, arch, cut } = collection;

  // Belt and braces. The server validates these; this is the last gate before
  // the value becomes a stylesheet, and it costs nothing.
  if (!slugSafe.test(slug ?? '')) return '';
  if (!hex.test(accent ?? '') || !hex.test(accentSoft ?? '')) return '';

  const archPx = Number.isFinite(arch) ? Math.max(0, Math.min(9999, arch)) : 0;
  const cutPx = Number.isFinite(cut) ? Math.max(0, Math.min(64, cut)) : 0;

  return `[data-world="${slug}"]{--accent:${accent};--accent-soft:${accentSoft};--world-arch:${archPx}px;--world-cut:${cutPx}px;}`;
};

export const CollectionStyles = () => {
  const { data } = useCollections();

  const css = (data ?? []).map(rule).filter(Boolean).join('');
  if (!css) return null;

  return <style data-collection-tokens>{css}</style>;
};

export default CollectionStyles;
